import React, { useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { FiEdit } from 'react-icons/fi';
import {
	requestInvByProtocol,
	requestSavedData,
	requestPAUpdate,
} from '../../../services/requests';
import { getLatestSavedRecord } from './investigacaoUtils';

const emptyInvestigacaoData = {
	coleta_dados: '',
	equipe: [],
	cronologia: [],
	planos: [],
	causas: {
		fator_paciente: '',
		fator_profissional: '',
		fator_tarefa: '',
		fator_comunicacao: '',
		fator_equipamento: '',
		fator_ambiente: '',
		fator_organizacional: '',
		fator_treinamento: '',
	},
};

const normalizeEmptyValue = (value) => {
	if (
		value === 'NÃO PREENCHIDO' ||
		value === '00:00:00' ||
		value === '2000-01-01' ||
		value === null ||
		value === undefined
	) {
		return '';
	}

	return value;
};

const formatDateBr = (value) => {
	if (!value) return '';
	const onlyDate = String(value).split('T')[0];
	const parts = onlyDate.split('-');
	if (parts.length !== 3) return value;
	const [year, month, day] = parts;
	return `${day}/${month}/${year}`;
};

const formatTimeBr = (value) => {
	if (!value) return '';
	const [hour = '', minute = ''] = String(value).split(':');
	if (!hour || !minute) return value;
	return `${hour}:${minute}`;
};

const formatStatus = (value) => {
	const statusMap = {
		PENDENTE: 'Pendente',
		EM_ANDAMENTO: 'Em andamento',
		CONCLUIDO: 'Concluído',
		CANCELADO: 'Cancelado',
	};

	return statusMap[value] || value || '';
};

const loadInvestigacaoData = async (notificacao) => {
	if (!notificacao?.protocolo) return emptyInvestigacaoData;

	const userCookie = Cookies.get('user');
	if (!userCookie) return emptyInvestigacaoData;

	const { token } = JSON.parse(userCookie);
	if (!token) return emptyInvestigacaoData;

	const invByProtocol = await requestInvByProtocol(
		token,
		notificacao.protocolo,
	);

	if (
		!Array.isArray(invByProtocol) ||
		invByProtocol.length === 0 ||
		invByProtocol?.error
	) {
		return emptyInvestigacaoData;
	}

	const invId = invByProtocol[0]?.id;
	if (!invId) return emptyInvestigacaoData;

	const [
		equipeSaved,
		planosSaved,
		cronologiaSaved,
		causasSaved,
		coletaSaved,
	] = await Promise.all([
		requestSavedData(token, invId, 'equipe'),
		requestSavedData(token, invId, 'planos-acao'),
		requestSavedData(token, invId, 'cronologia'),
		requestSavedData(token, invId, 'causas'),
		requestSavedData(token, invId, 'coleta-dados'),
	]);
	const latestCausas = getLatestSavedRecord(causasSaved);

	return {
		coleta_dados:
			Array.isArray(coletaSaved) && coletaSaved.length > 0
				? normalizeEmptyValue(coletaSaved[0]?.dados_coletados)
				: '',
		equipe:
			Array.isArray(equipeSaved) && equipeSaved.length > 0
				? equipeSaved.map((membro) => ({
						nome_completo: normalizeEmptyValue(
							membro.nome_completo,
						),
						cargo: normalizeEmptyValue(membro.cargo),
						setor: normalizeEmptyValue(membro.setor),
						email: normalizeEmptyValue(membro.email),
					}))
				: [],
		planos:
			Array.isArray(planosSaved) && planosSaved.length > 0
				? planosSaved.map((plano) => ({
						id: plano.id,
						causas_prioritarias: normalizeEmptyValue(
							plano.causas_prioritarias,
						),
						acoes_planejadas: normalizeEmptyValue(
							plano.acoes_planejadas,
						),
						responsavel: normalizeEmptyValue(
							plano.responsabilidade,
						),
						prazo: normalizeEmptyValue(
							plano.prazo === 32767 ? '' : plano.prazo,
						),
						status: normalizeEmptyValue(plano.status),
					}))
				: [],
		cronologia:
			Array.isArray(cronologiaSaved) && cronologiaSaved.length > 0
				? [...cronologiaSaved]
						.sort((a, b) => a.id - b.id)
						.map((cron) => ({
							evento: normalizeEmptyValue(cron.evento),
							data: normalizeEmptyValue(cron.data_evento),
							hora: normalizeEmptyValue(cron.hora_evento),
							setor: normalizeEmptyValue(cron.local),
							descricao: normalizeEmptyValue(cron.descricao),
						}))
				: [],
		causas:
			latestCausas
				? {
						fator_paciente: normalizeEmptyValue(
							latestCausas.fatores_paciente,
						),
						fator_profissional: normalizeEmptyValue(
							latestCausas.fatores_profissionais,
						),
						fator_tarefa: normalizeEmptyValue(
							latestCausas.fatores_tarefa,
						),
						fator_comunicacao: normalizeEmptyValue(
							latestCausas.fatores_comunicacao,
						),
						fator_equipe: normalizeEmptyValue(
							latestCausas.fatores_equipe,
						),
						fator_equipamento: normalizeEmptyValue(
							latestCausas.fatores_equipamento,
						),
						fator_ambiente: normalizeEmptyValue(
							latestCausas.fatores_ambiente,
						),
						fator_organizacional: normalizeEmptyValue(
							latestCausas.fatores_organizacionais,
						),
						fator_treinamento: normalizeEmptyValue(
							latestCausas.fatores_treinamento,
						),
					}
				: emptyInvestigacaoData.causas,
	};
};

const DisplayField = ({ label, value, className = '' }) => (
	<div className={`flex flex-col gap-1 ${className}`}>
		<p className='text-[#004A80] font-medium'>{label}</p>
		<div
			className='h-full min-h-[40px] flex items-center px-2 py-2 border-[1px] rounded-md border-[#E3E3E3] whitespace-pre-wrap'
			style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
			{value || 'Não preenchido'}
		</div>
	</div>
);

const RepeatedRowsWrapper = ({ children }) => (
	<div className='p-3 flex flex-col gap-3'>{children}</div>
);

const RepeatedRow = ({ children, columnsClass = '' }) => (
	<div
		className={`border border-[#E3E3E3] rounded-md p-3 grid gap-3 items-stretch ${columnsClass}`}>
		{children}
	</div>
);

const STATUS_OPTIONS = [
	{ value: 'PENDENTE', label: 'Pendente' },
	{ value: 'EM_ANDAMENTO', label: 'Em andamento' },
	{ value: 'CONCLUIDO', label: 'Concluído' },
	{ value: 'CANCELADO', label: 'Cancelado' },
];

export default function NewInvestigacaoPreenchido({ notificacao, viewOnly = false }) {
	const [formData, setFormData] = useState(emptyInvestigacaoData);
	const [editingStatusId, setEditingStatusId] = useState(null);
	const [editingStatusValue, setEditingStatusValue] = useState('');
	const [savingStatusId, setSavingStatusId] = useState(null);
	const protocolo = notificacao?.protocolo;

	const userCookieRaw = Cookies.get('user');
	const parsedUser = userCookieRaw ? JSON.parse(userCookieRaw) : null;
	const canEditPlanStatus =
		parsedUser?.permissao?.includes('Classificadores') && !viewOnly;

	const handleEditStatus = (plano) => {
		setEditingStatusId(plano.id);
		setEditingStatusValue(plano.status || 'PENDENTE');
	};

	const handleSaveStatus = async (planoId) => {
		if (!parsedUser?.token) return;
		setSavingStatusId(planoId);
		try {
			const result = await requestPAUpdate(
				parsedUser.token,
				{ status: editingStatusValue },
				planoId,
			);
			if (!result?.error) {
				setFormData((prev) => ({
					...prev,
					planos: prev.planos.map((p) =>
						p.id === planoId
							? { ...p, status: editingStatusValue }
							: p,
					),
				}));
			}
		} catch (e) {
			console.error('Erro ao atualizar status:', e);
		} finally {
			setSavingStatusId(null);
			setEditingStatusId(null);
		}
	};

	useEffect(() => {
		const handleLoad = async () => {
			try {
				const result = await loadInvestigacaoData({ protocolo });
				setFormData(result);
			} catch (error) {
				console.error('Erro ao carregar investigação:', error);
			}
		};

		handleLoad();
	}, [protocolo]);

	const fatores = [
		{ label: 'Fatores do paciente', value: formData.causas.fator_paciente },
		{
			label: 'Fatores dos profissionais',
			value: formData.causas.fator_profissional,
		},
		{
			label: 'Fatores da tarefa / atividade',
			value: formData.causas.fator_tarefa,
		},
		{
			label: 'Fatores de comunicação',
			value: formData.causas.fator_comunicacao,
		},
		{
			label: 'Fatores de equipamentos',
			value: formData.causas.fator_equipamento,
		},
		{ label: 'Fatores do ambiente', value: formData.causas.fator_ambiente },
		{
			label: 'Fatores organizacionais',
			value: formData.causas.fator_organizacional,
		},
		{
			label: 'Fatores de treinamento / educação',
			value: formData.causas.fator_treinamento,
		},
	];

	return (
		<div className='max-w-[1136px] font-inter bg-white'>
			<section className='border border-[#E3E3E3] rounded-md mt-[16px]'>
				<h2 className='text-[#004A80] bg-[#F4F4F4] rounded-t-md text-[18px] text-center font-semibold border-[#E3E3E3] border-b-[1px] py-1'>
					EQUIPE DE INVESTIGAÇÃO
				</h2>
				<RepeatedRowsWrapper>
					{formData.equipe.length > 0 ? (
						formData.equipe.map((membro, index) => (
							<RepeatedRow
								key={`${membro.email || membro.nome_completo}-${index}`}
								columnsClass='grid-cols-1 md:grid-cols-2 xl:grid-cols-4'>
								<DisplayField
									label='Nome'
									value={membro.nome_completo}
								/>
								<DisplayField
									label='Cargo'
									value={membro.cargo}
								/>
								<DisplayField
									label='Setor'
									value={membro.setor}
								/>
								<DisplayField
									label='Email'
									value={membro.email}
								/>
							</RepeatedRow>
						))
					) : (
						<p className='text-[#8b8b8b]'>
							Nenhum membro informado.
						</p>
					)}
				</RepeatedRowsWrapper>
			</section>

			<section className='border border-[#E3E3E3] rounded-md mt-[16px]'>
				<div className='text-[#004A80] bg-[#F4F4F4] rounded-t-md text-[18px] text-center font-semibold border-[#E3E3E3] border-b-[1px] py-1'>
					<h2>COLETA E ORGANIZAÇÃO DE DADOS</h2>
					<p className='text-[16px] font-normal'>
						Utilizar metodologia apropriada: entrevistas, consulta a
						prontuários ou documentos da instituição, busca ativa em
						campo, entre outras.
					</p>
				</div>
				<div className='p-3'>
					<DisplayField
						label='Descrição da coleta de dados'
						value={formData.coleta_dados}
					/>
				</div>
			</section>

			<section className='border border-[#E3E3E3] rounded-md mt-[16px]'>
				<h2 className='text-[#004A80] bg-[#F4F4F4] rounded-t-md text-[18px] text-center font-semibold border-[#E3E3E3] border-b-[1px] py-1'>
					CRONOLOGIA DO INCIDENTE
				</h2>
				<RepeatedRowsWrapper>
					{formData.cronologia.length > 0 ? (
						formData.cronologia.map((item, index) => (
							<RepeatedRow
								key={`${item.evento}-${index}`}
								columnsClass='grid-cols-1 md:grid-cols-2 xl:grid-cols-[1fr_110px_80px_1fr_2fr]'>
								<DisplayField
									label='Evento'
									value={item.evento}
								/>
								<DisplayField
									label='Data'
									value={formatDateBr(item.data)}
								/>
								<DisplayField
									label='Hora'
									value={formatTimeBr(item.hora)}
								/>
								<DisplayField
									label='Local / Setor'
									value={item.setor}
								/>
								<DisplayField
									label='Descrição'
									value={item.descricao}
									className='xl:col-span-1'
								/>
							</RepeatedRow>
						))
					) : (
						<p className='text-[#8b8b8b]'>
							Nenhum evento informado.
						</p>
					)}
				</RepeatedRowsWrapper>
			</section>

			<section className='border border-[#E3E3E3] rounded-md mt-[16px]'>
				<div className='text-[#004A80] bg-[#F4F4F4] rounded-t-md text-[18px] text-center font-semibold border-[#E3E3E3] border-b-[1px] py-1'>
					<h2>
						DEFINIÇÃO DAS CAUSAS - FATORES QUE CONTRIBUÍRAM PARA O
						INCIDENTE
					</h2>
				</div>
				<div className='p-3 grid grid-cols-1 md:grid-cols-2 gap-3'>
					{fatores.map((fator) => (
						<DisplayField
							key={fator.label}
							label={fator.label}
							value={fator.value}
						/>
					))}
				</div>
			</section>

			<section className='border border-[#E3E3E3] rounded-md mt-[16px]'>
				<h2 className='text-[#004A80] bg-[#F4F4F4] rounded-t-md text-[18px] text-center font-semibold border-[#E3E3E3] border-b-[1px] py-1'>
					PLANO DE AÇÃO
				</h2>
				<RepeatedRowsWrapper>
					{formData.planos.length > 0 ? (
						formData.planos.map((plano, index) => (
							<RepeatedRow
								key={`${plano.responsavel}-${index}`}
								columnsClass='grid-cols-1 md:grid-cols-2 xl:grid-cols-5'>
								<DisplayField
									label='Causas prioritárias'
									value={plano.causas_prioritarias}
								/>
								<DisplayField
									label='Ações planejadas'
									value={plano.acoes_planejadas}
								/>
								<DisplayField
									label='Responsável'
									value={plano.responsavel}
								/>
								<DisplayField
									label='Prazo (dias)'
									value={String(plano.prazo || '')}
								/>
								<div className='flex flex-col gap-1'>
									<p className='text-[#004A80] font-medium'>
										Status
									</p>
									{canEditPlanStatus &&
									editingStatusId === plano.id ? (
										<div className='flex gap-2 items-center'>
											<select
												value={editingStatusValue}
												onChange={(e) =>
													setEditingStatusValue(
														e.target.value,
													)
												}
												className='flex-1 h-9 border border-gray-300 rounded-md px-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'>
												{STATUS_OPTIONS.map((opt) => (
													<option
														key={opt.value}
														value={opt.value}>
														{opt.label}
													</option>
												))}
											</select>
											<button
												onClick={() =>
													handleSaveStatus(plano.id)
												}
												disabled={
													savingStatusId === plano.id
												}
												className='bg-[#cfcfcf] hover:bg-[#a8a8a8] text-white px-[7px] rounded-md shadow-sm transition-all duration-200 font-bold text-lg flex-shrink-0'>
												{savingStatusId === plano.id
													? '...'
													: '✓'}
											</button>
										</div>
									) : (
										<div className='min-h-[40px] flex items-center justify-between gap-2 px-2 py-2 border-[1px] rounded-md border-[#E3E3E3]'>
											<span>
												{formatStatus(plano.status) ||
													'Não preenchido'}
											</span>
											{canEditPlanStatus && (
												<button
													onClick={() =>
														handleEditStatus(plano)
													}
													title='Editar status'
													className='bg-[#bdbdbd] shadow-md hover:bg-[#adadad] text-white p-1 rounded-md transition-all duration-200'>
													<FiEdit className='h-4 w-4' />
												</button>
											)}
										</div>
									)}
								</div>
							</RepeatedRow>
						))
					) : (
						<p className='text-[#8b8b8b]'>
							Nenhum plano de ação informado.
						</p>
					)}
				</RepeatedRowsWrapper>
			</section>
		</div>
	);
}
